import { Link } from "@tanstack/react-router";
import { useId, type ReactNode } from "react";
import { cn } from "@/lib/utils";
import { Container, Eyebrow, Lead } from "@/components/site/primitives";

/**
 * Shared page section primitives.
 *
 * Every content page composes these so vertical rhythm, dividers, surfaces and
 * heading semantics stay identical across the site:
 *   spacing="default" -> 40px mobile / 64px desktop (Container default)
 *   spacing="compact" -> 32px mobile / 40px desktop (stat, logo and note bands)
 */

type Tone = "default" | "surface" | "card";
type Divider = "none" | "top" | "bottom" | "both";
type Spacing = "default" | "compact" | "none";

const toneClass: Record<Tone, string> = {
  default: "bg-background",
  surface: "bg-surface",
  card: "bg-card",
};

const dividerClass: Record<Divider, string> = {
  none: "",
  top: "border-t border-border",
  bottom: "border-b border-border",
  both: "border-y border-border",
};

const spacingClass: Record<Spacing, string> = {
  default: "",
  compact: "py-8 md:py-10",
  none: "py-0 md:py-0",
};

export function Section({
  children,
  heading,
  headingId: headingIdProp,
  headingLevel = 2,
  hiddenHeading = false,
  eyebrow,
  lead,
  tone = "default",
  divider = "top",
  spacing = "default",
  className,
  containerClassName,
  bodyClassName,
}: {
  children?: ReactNode;
  heading?: ReactNode;
  headingId?: string;
  headingLevel?: 2 | 3;
  hiddenHeading?: boolean;
  eyebrow?: ReactNode;
  lead?: ReactNode;
  tone?: Tone;
  divider?: Divider;
  spacing?: Spacing;
  className?: string;
  containerClassName?: string;
  bodyClassName?: string;
}) {
  const generatedId = useId();
  const headingId = headingIdProp ?? generatedId;
  const Heading = headingLevel === 3 ? "h3" : "h2";

  return (
    <section
      aria-labelledby={heading ? headingId : undefined}
      className={cn(dividerClass[divider], toneClass[tone], className)}
    >
      <Container className={cn(spacingClass[spacing], containerClassName)}>
        {heading ? (
          hiddenHeading ? (
            <Heading id={headingId} className="sr-only">
              {heading}
            </Heading>
          ) : (
            <header className="max-w-[68ch]">
              {eyebrow ? <Eyebrow>{eyebrow}</Eyebrow> : null}
              <Heading
                id={headingId}
                className={cn(
                  "text-2xl font-bold tracking-[-0.03em] md:text-[32px]",
                  eyebrow ? "mt-3" : undefined,
                )}
              >
                {heading}
              </Heading>
              {lead ? <Lead className="mt-3">{lead}</Lead> : null}
            </header>
          )
        ) : null}
        {children ? (
          <div className={cn(heading && !hiddenHeading ? "mt-8" : undefined, bodyClassName)}>
            {children}
          </div>
        ) : null}
      </Container>
    </section>
  );
}

export type Stat = {
  stat: string;
  label: string;
  /** How the figure was delivered: role and engagement type. */
  context?: string;
  /** Evidence page for the figure. */
  to?: string;
  hash?: string;
  linkLabel?: string;
  accent?: boolean;
};

/** Full-bleed band of headline numbers, divided by rules rather than padding. */
export function StatBand({
  stats,
  heading = "Results",
  headingId,
  divider = "bottom",
}: {
  stats: Stat[];
  heading?: string;
  headingId?: string;
  divider?: Divider;
}) {
  return (
    <Section heading={heading} headingId={headingId} hiddenHeading divider={divider} spacing="none">
      <ul className="grid md:grid-cols-3">
        {stats.map((item, index) => (
          <li
            key={item.stat}
            className={
              index === 0
                ? "py-8 md:py-10 md:pr-8"
                : "border-t border-border py-8 md:border-t-0 md:border-l md:px-8 md:py-10 md:last:pr-0"
            }
          >
            <p
              className={cn(
                "text-[32px] leading-none font-bold tracking-[-0.035em] md:text-[40px]",
                item.accent ? "text-primary" : "text-ink",
              )}
            >
              {item.stat}
            </p>
            <p className="mt-3 max-w-[34ch] text-[15px] leading-relaxed font-semibold text-ink">
              {item.label}
            </p>
            {item.context ? (
              <p className="mt-2 max-w-[34ch] text-[15px] leading-relaxed text-body">
                {item.context}
              </p>
            ) : null}
            {item.to ? (
              <p className="mt-3 text-sm">
                <Link
                  to={item.to}
                  hash={item.hash}
                  className="font-semibold text-primary underline underline-offset-4"
                >
                  {item.linkLabel ?? "See the evidence"}
                </Link>
              </p>
            ) : null}
          </li>
        ))}
      </ul>
    </Section>
  );
}

export type PullQuote = { quote: string; name: string; role: string };

function initials(name: string) {
  return name
    .split(" ")
    .map((part) => part[0])
    .join("");
}

/** Short one-line client quotes with avatar initials, aligned to a shared baseline. */
export function PullQuoteGrid({ quotes, className }: { quotes: PullQuote[]; className?: string }) {
  return (
    <ul className={cn("grid gap-8 md:grid-cols-3 md:gap-10", className)}>
      {quotes.map((item) => (
        <li key={item.name} className="h-full">
          <figure className="flex h-full flex-col justify-between">
            <blockquote className="text-[17px] leading-snug font-bold tracking-[-0.02em] text-ink md:text-lg">
              &ldquo;{item.quote}&rdquo;
            </blockquote>
            <figcaption className="mt-5 flex items-center gap-3">
              <span
                aria-hidden="true"
                className="grid size-9 shrink-0 place-items-center rounded-full bg-border text-xs font-semibold text-muted-foreground"
              >
                {initials(item.name)}
              </span>
              <span className="min-w-0 text-sm leading-tight">
                <span className="block font-semibold text-ink">{item.name}</span>
                <span className="block text-muted-foreground">{item.role}</span>
              </span>
            </figcaption>
          </figure>
        </li>
      ))}
    </ul>
  );
}

export type SummaryCard = { title: string; short: string };

/** Even grid of short summary cards, used for service and capability lists. */
export function CardGrid({
  items,
  columns = 4,
  className,
}: {
  items: SummaryCard[];
  columns?: 2 | 3 | 4;
  className?: string;
}) {
  const columnClass =
    columns === 2
      ? "sm:grid-cols-2"
      : columns === 3
        ? "sm:grid-cols-2 lg:grid-cols-3"
        : "sm:grid-cols-2 lg:grid-cols-4";
  return (
    <ul className={cn("grid gap-5", columnClass, className)}>
      {items.map((item) => (
        <li key={item.title} className="rounded-lg border border-border bg-card p-6">
          <h3 className="text-lg font-semibold">{item.title}</h3>
          <p className="mt-2 text-[15px] leading-relaxed text-body">{item.short}</p>
        </li>
      ))}
    </ul>
  );
}

/**
 * Boxed closing call to action. Unlike CtaBand it sits on a raised panel and can
 * link to a hash target, so it works as the last block on a content page.
 */
export function CtaPanel({
  heading,
  body,
  to,
  hash,
  cta,
  headingId,
  onCtaClick,
  divider = "top",
}: {
  heading: string;
  body: string;
  to: string;
  hash?: string;
  cta: string;
  headingId?: string;
  onCtaClick?: () => void;
  divider?: Divider;
}) {
  const generatedId = useId();
  const id = headingId ?? generatedId;
  return (
    <section aria-labelledby={id} className={dividerClass[divider]}>
      <Container>
        <div className="flex flex-col gap-6 rounded-[10px] border border-border bg-card p-6 shadow-panel md:flex-row md:items-center md:justify-between md:p-9">
          <div className="min-w-0">
            <h2 id={id} className="text-2xl font-bold tracking-[-0.03em] md:text-[32px]">
              {heading}
            </h2>
            <p className="mt-3 max-w-[52ch] text-base leading-relaxed text-body">{body}</p>
          </div>
          <Link
            to={to}
            hash={hash}
            onClick={onCtaClick}
            className="inline-flex shrink-0 items-center gap-2 self-start rounded-lg bg-primary px-6 py-3.5 text-[15px] font-semibold text-primary-foreground transition-opacity hover:opacity-90 md:self-auto"
          >
            {cta} <span aria-hidden="true">&rarr;</span>
          </Link>
        </div>
      </Container>
    </section>
  );
}
